import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Maximize2, Info, Loader, Contrast } from 'lucide-react';
import { documentApi } from '../../services/api';
import { tokenStorage } from '../../utils/tokenStorage';
import type { DicomStudy, DicomInstanceInfo } from '../../types';
import { DicomMetadataPanel } from './DicomMetadataPanel';

// Lazy-loaded Cornerstone modules (populated on first init)
let csCore: typeof import('@cornerstonejs/core') | null = null;
let csInitialized = false;

const RENDERING_ENGINE_ID = 'dicom-viewer-engine';
const VIEWPORT_ID = 'dicom-stack-viewport';

async function initCornerstone(): Promise<void> {
  if (csInitialized) return;

  const [core, imageLoader] = await Promise.all([
    import('@cornerstonejs/core'),
    import('@cornerstonejs/dicom-image-loader'),
  ]);

  csCore = core;

  await core.init();
  imageLoader.init({
    maxWebWorkers: Math.min(navigator.hardwareConcurrency || 1, 4),
    beforeSend: (
      _xhr: XMLHttpRequest,
      _imageId: string,
      _defaultHeaders: Record<string, string>,
    ) => {
      const token = tokenStorage.getToken();
      if (token) {
        return { Authorization: `Bearer ${token}` };
      }
      return {};
    },
  });

  csInitialized = true;
}

interface DicomViewerProps {
  study: DicomStudy;
  instances: DicomInstanceInfo[];
  onClose: () => void;
}

const WW_PRESETS: Record<string, { center: number; width: number; label: string }> = {
  soft: { center: 40, width: 400, label: 'Soft Tissue' },
  lung: { center: -600, width: 1500, label: 'Lung' },
  bone: { center: 300, width: 1500, label: 'Bone' },
  brain: { center: 40, width: 80, label: 'Brain' },
};

export const DicomViewer: React.FC<DicomViewerProps> = ({ study, instances, onClose }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<InstanceType<typeof import('@cornerstonejs/core').RenderingEngine> | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMetadata, setShowMetadata] = useState(false);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  const currentInstance = instances[currentIndex];

  // Build wadouri image IDs
  const imageIds = useRef(
    instances.map(inst => `wadouri:${documentApi.getDicomInstanceUrl(study.id, inst.id)}`)
  );

  // Initialize Cornerstone and set up the viewport
  useEffect(() => {
    let destroyed = false;

    const setup = async () => {
      if (!viewportRef.current) return;

      try {
        await initCornerstone();
        if (destroyed || !csCore || !viewportRef.current) return;

        const engine = new csCore.RenderingEngine(RENDERING_ENGINE_ID);
        engineRef.current = engine;

        engine.enableElement({
          viewportId: VIEWPORT_ID,
          element: viewportRef.current,
          type: csCore.Enums.ViewportType.STACK,
        });

        const viewport = engine.getViewport(VIEWPORT_ID) as import('@cornerstonejs/core').Types.IStackViewport;
        await viewport.setStack(imageIds.current, 0);
        viewport.render();

        setLoading(false);
      } catch (err: any) {
        console.error('Cornerstone initialization failed:', err);
        if (!destroyed) {
          setError(`Failed to initialise DICOM viewer: ${err.message || 'Unknown error'}`);
          setLoading(false);
        }
      }
    };

    setup();

    return () => {
      destroyed = true;
      if (engineRef.current) {
        engineRef.current.destroy();
        engineRef.current = null;
      }
    };
  }, []); // Only run once on mount

  // Helper to get the current viewport
  const getViewport = useCallback(() => {
    if (!engineRef.current || !csCore) return null;
    return engineRef.current.getViewport(VIEWPORT_ID) as import('@cornerstonejs/core').Types.IStackViewport;
  }, []);

  // Navigate to a specific slice
  const goToSlice = useCallback(async (index: number) => {
    const clamped = Math.max(0, Math.min(instances.length - 1, index));
    setCurrentIndex(clamped);

    const viewport = getViewport();
    if (viewport) {
      await viewport.setImageIdIndex(clamped);
    }
  }, [instances.length, getViewport]);

  // Apply a W/L preset
  const applyWindowLevel = useCallback((presetKey: string) => {
    const preset = WW_PRESETS[presetKey];
    if (!preset || !csCore) return;

    const viewport = getViewport();
    if (!viewport) return;

    const { lower, upper } = csCore.utilities.windowLevel.toLowHighRange(preset.width, preset.center);
    viewport.setProperties({ voiRange: { lower, upper } });
    viewport.render();
    setActivePreset(presetKey);
  }, [getViewport]);

  // Zoom controls
  const applyZoom = useCallback((newZoom: number) => {
    const viewport = getViewport();
    if (!viewport) return;

    const clamped = Math.max(0.25, Math.min(5, newZoom));
    viewport.setZoom(clamped);
    viewport.render();
    setZoom(clamped);
  }, [getViewport]);

  const resetView = useCallback(() => {
    const viewport = getViewport();
    if (!viewport) return;

    viewport.resetCamera({ resetPan: true, resetZoom: true });
    viewport.render();
    setZoom(1);
    setActivePreset(null);
  }, [getViewport]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          goToSlice(currentIndex - 1);
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          goToSlice(currentIndex + 1);
          break;
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          applyZoom(zoom + 0.25);
          break;
        case '-':
          applyZoom(zoom - 0.25);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, zoom, goToSlice, applyZoom, onClose]);

  // Mouse wheel for scrolling through slices
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 1 : -1;
    goToSlice(currentIndex + delta);
  }, [currentIndex, goToSlice]);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 text-white">
        <div className="flex items-center gap-4">
          <h3 className="font-medium text-sm">
            {study.modality} — {study.studyDescription || study.bodyPartExamined || 'DICOM Study'}
          </h3>
          <span className="text-xs text-gray-400">
            {study.patientName} {study.studyDate ? `| ${study.studyDate}` : ''}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Slice position */}
          <span className="text-xs text-gray-400 mr-4">
            Slice {currentIndex + 1} / {instances.length}
          </span>

          {/* WW/WL presets */}
          {Object.entries(WW_PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => applyWindowLevel(key)}
              className={`px-2 py-1 text-xs rounded ${
                activePreset === key
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {preset.label}
            </button>
          ))}

          {/* Zoom controls */}
          <button onClick={() => applyZoom(zoom - 0.25)} className="p-1 hover:bg-gray-700 rounded">
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => applyZoom(zoom + 0.25)} className="p-1 hover:bg-gray-700 rounded">
            <ZoomIn className="w-4 h-4" />
          </button>
          <button onClick={resetView} className="p-1 hover:bg-gray-700 rounded" title="Reset view">
            <Maximize2 className="w-4 h-4" />
          </button>

          {/* Metadata toggle */}
          <button
            onClick={() => setShowMetadata(!showMetadata)}
            className={`p-1 rounded ${showMetadata ? 'bg-cyan-600' : 'hover:bg-gray-700'}`}
          >
            <Info className="w-4 h-4" />
          </button>

          {/* Close */}
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded ml-2">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Viewport area */}
        <div
          className="flex-1 relative overflow-hidden bg-black"
          onWheel={handleWheel}
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <Loader className="w-8 h-8 text-cyan-500 animate-spin" />
              <span className="ml-3 text-white text-sm">Initialising DICOM viewer...</span>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="text-center text-gray-400 p-8">
                <Contrast className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">{error}</p>
                <p className="text-xs mt-2 text-gray-500">
                  Instance: {currentInstance?.sopInstanceUid?.slice(0, 20)}...
                </p>
              </div>
            </div>
          )}

          {/* Cornerstone.js renders into this div */}
          <div
            ref={viewportRef}
            style={{ width: '100%', height: '100%' }}
          />

          {/* Slice navigation arrows */}
          {instances.length > 1 && !loading && (
            <>
              <button
                onClick={() => goToSlice(currentIndex - 1)}
                disabled={currentIndex === 0}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white disabled:opacity-30 hover:bg-black/70 z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => goToSlice(currentIndex + 1)}
                disabled={currentIndex === instances.length - 1}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white disabled:opacity-30 hover:bg-black/70 z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
        </div>

        {/* Metadata panel */}
        {showMetadata && (
          <DicomMetadataPanel study={study} currentInstance={currentInstance} />
        )}
      </div>

      {/* Slice slider */}
      {instances.length > 1 && (
        <div className="px-4 py-3 bg-gray-900 border-t border-gray-700">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 font-medium w-8 text-right">{currentIndex + 1}</span>
            <input
              type="range"
              min={0}
              max={instances.length - 1}
              value={currentIndex}
              onChange={(e) => goToSlice(parseInt(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-700 accent-cyan-500"
            />
            <span className="text-xs text-gray-400 font-medium w-8">{instances.length}</span>
          </div>
        </div>
      )}
    </div>
  );
};
